// The Execute-time backstop (editable-plan spec Part 1a; contract 1.5). Pure: no React, no
// I/O. prepareRows revalidates every row (any org member can rewrite run-doc fields), applies
// the org model policy, refuses a custom model that is no longer a verified model of the row's
// family (never re-routing it to a built-in), fits the run total to the plan max with the spec
// targets' priorities, and stamps dispatchTarget. Its result is what Execute writes to
// executedRows and what runRows dispatches. The message helpers keep one wording for the
// planned-steps notes, the rejected-row lines and the "nothing to run" errors.
import { validateRowParams, runTotalItemForRow, cloneJson } from './rowContract';
import { GT4SD_CHECKPOINTS } from '../../config/gt4sdCheckpoints';
import { normalizeModelPolicy } from '../../config/capabilities';
import { PLAN_LIMITS } from '../../config/planLimits';
import { fitRunTotal, chargeOf } from '../router/fitRunTotal';
import { dispatchTargetForRow } from '../../context/customModelSelection';

const SERVICE_LABEL = Object.freeze({ mattergen: 'MatterGen', fm4m: 'FM4M', gt4sd: 'GT4SD' });
// The names PolicyNotes uses today (PlannedStepsTable.jsx SERVICE_LABELS), so skippedNoteText
// reads exactly like the table it replaces in 1a-06.
const POLICY_SERVICE_LABEL = Object.freeze({ mattergen: 'MatterGen', fm4m: 'IBM FM4M', gt4sd: 'GT4SD' });

export const NOTHING_RUNNABLE_MESSAGE = 'Nothing to run: no model your organization allows can serve this plan.';

const own = (table, key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key);

function isPlainObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * "MatterGen: dft_band_gap", plus " (custom)" for a custom row. A value that is not a plain
 * object with a known service and a string target (a tampered stored row) is named by its id,
 * else "Unknown step". Never throws.
 */
export function rowLabel(row) {
  try {
    if (isPlainObject(row) && own(SERVICE_LABEL, row.service) && typeof row.target === 'string') {
      return `${SERVICE_LABEL[row.service]}: ${row.target}${row.customModelId ? ' (custom)' : ''}`;
    }
  } catch (_) { /* a hostile getter: fall back to the id */ }
  try {
    const id = row == null ? undefined : row.id;
    if (id !== undefined && id !== null && id !== '') return String(id);
  } catch (_) { /* fall through */ }
  return 'Unknown step';
}

/** One router `skipped` entry ({ service, domain, reason, fallback? }) as one sentence. */
export function skippedNoteText(entry) {
  const e = entry || {};
  const name = own(POLICY_SERVICE_LABEL, e.service) ? POLICY_SERVICE_LABEL[e.service] : String(e.service || 'A model');
  if (e.reason === 'plan_max') return `${name} was left out: your plan's per-run candidate limit has no room for it.`;
  const fallbackName = own(POLICY_SERVICE_LABEL, e.fallback) ? POLICY_SERVICE_LABEL[e.fallback] : e.fallback;
  const routed = e.fallback ? `; routed to ${fallbackName} instead` : '';
  return e.reason === 'zero_weight'
    ? `${name} is weighted to zero by your organization's model policy, so it is blocked like a disabled model${routed}.`
    : `${name} is disabled by your organization's model policy${routed}.`;
}

/**
 * Why nothing can run: the rejection messages, else one sentence per router skipped entry,
 * else NOTHING_RUNNABLE_MESSAGE. Never ''. NL Execute, manual submit and Re-run stop with it
 * when prepared.rows is empty.
 */
export function nothingRunnableMessage(prepared, skipped = []) {
  const rejected = prepared && Array.isArray(prepared.rejected) ? prepared.rejected : [];
  const fromRejected = rejected.map((r) => (r && typeof r.message === 'string' ? r.message : '')).filter(Boolean);
  if (fromRejected.length > 0) return fromRejected.join(' ');
  const fromSkipped = (Array.isArray(skipped) ? skipped : []).map(skippedNoteText).filter(Boolean);
  if (fromSkipped.length > 0) return fromSkipped.join(' ');
  return NOTHING_RUNNABLE_MESSAGE;
}

function rejectionMessage(reason, row, detail) {
  const label = rowLabel(row);
  const service = isPlainObject(row) && own(SERVICE_LABEL, row.service) ? SERVICE_LABEL[row.service] : label;
  switch (reason) {
    case 'model_unavailable':
      return `GT4SD ${row.target} was not run: it has no available checkpoint.`;
    case 'invalid_params':
      return `${label} was not run: its settings are not valid (${detail}).`;
    case 'org_policy':
      return `${service} was not run: it is disabled for your organization.`;
    case 'zero_weight':
      return `${service} was not run: your organization's model policy weights it to zero.`;
    case 'custom_model_unavailable':
      return `${label} was not run: its custom model is not available to your organization.`;
    case 'plan_max':
      return `${label} was not run: your plan's per-run candidate limit has no room for it.`;
    default:
      return `${label} was not run.`;
  }
}

/**
 * @param {Row[]} rows
 * @param {{ customModels, modelPolicy, planMax, byocActive, byocEndpoints, targets? }} ctx
 *   targets: spec.targets, read ONLY for rowPriority when the backstop fit has leftovers.
 * @returns {{ rows: Row[], rejected: Array<{ rowId, reason, message }>, scaled: boolean }}
 */
export function prepareRows(rows, ctx = {}) {
  const c = ctx || {};
  const list = Array.isArray(rows) ? rows : [];
  const policy = normalizeModelPolicy(c.modelPolicy);
  const customModels = Array.isArray(c.customModels) ? c.customModels : [];
  const max = Math.floor(Number(c.planMax));
  const planMax = Number.isFinite(max) && max > 0 ? max : PLAN_LIMITS.pilot.maxCandidatesPerRun;
  const targets = Array.isArray(c.targets) ? c.targets : [];
  const rejected = [];
  const accepted = [];

  list.forEach((row, index) => {
    const rowId = isPlainObject(row) && typeof row.id === 'string' && row.id ? row.id : `invalid-${index + 1}`;
    const reject = (reason, detail) => rejected.push({ rowId, reason, message: rejectionMessage(reason, row, detail) });
    // 0. An algorithm with no live checkpoint (1.2.6) says so before any param error.
    if (isPlainObject(row) && row.service === 'gt4sd' && typeof row.target === 'string') {
      const ids = own(GT4SD_CHECKPOINTS, row.target) ? GT4SD_CHECKPOINTS[row.target] : undefined;
      if (!Array.isArray(ids) || ids.length === 0) { reject('model_unavailable'); return; }
    }
    // 1. Every row is revalidated against the allowlist.
    const check = validateRowParams(row);
    if (!check.ok) { reject('invalid_params', (check.errors && check.errors[0]) || 'not a valid row'); return; }
    // 2. The org model policy (settled decision 5): disabled, or weighted to zero.
    const p = policy[row.service];
    if (!p || !p.enabled) { reject('org_policy'); return; }
    if (!(p.weight > 0)) { reject('zero_weight'); return; }
    // 3. A custom row runs only on a verified model of its own family; it is never re-routed.
    if (row.customModelId != null && !customModels.some((m) => m && m.id === row.customModelId && m.status === 'verified' && m.family === row.service)) {
      reject('custom_model_unavailable');
      return;
    }
    accepted.push(cloneJson(row));
  });

  // 4. Backstop fit: the same fitRunTotal the router's prefill used, ranked by the spec
  //    targets' priorities. Representation rows charge 0 and keep their seed count.
  const items = accepted.map((row) => runTotalItemForRow(row, targets));
  const fit = fitRunTotal(items, planMax);
  const out = [];
  accepted.forEach((row, i) => {
    if (fit.scaled && row.kind !== 'represent') {
      const fitted = fit.items[i].budget;
      if (chargeOf(items[i]) > 0 && !(fitted > 0)) {
        rejected.push({ rowId: row.id, reason: 'plan_max', message: rejectionMessage('plan_max', row) });
        return;
      }
      if (fitted !== row.candidates) {
        row.adjustments = { ...(row.adjustments || {}), scaledFrom: row.candidates };
        row.candidates = fitted;
      }
    }
    // 5. Where the row goes, in the dispatchTarget vocabulary (rowEndpoint agrees).
    row.dispatchTarget = dispatchTargetForRow(row, { customModels, byocActive: !!c.byocActive, byocEndpoints: c.byocEndpoints || {} });
    out.push(row);
  });
  return { rows: out, rejected, scaled: fit.scaled };
}
